/**
 * Dashboard Translations Module (English)
 * 
 * Contains: Dashboard, Index (Landing), Charts, Header, Metrics, Global Presence
 * 
 * @module dashboard
 * @language en
 */

export const dashboard = {
  index: {
    heroTitle: "JCI Development Councils Youth Futures Observatory",
    heroSubtitle: "Measuring Youth Leadership, Mental Health & Economic Opportunity Globally",
    heroDescription: "A data-driven platform analyzing the conditions for young leaders across 114 countries. Using verified metrics from JCI's global network and research partnerships.",
    globalImpactTitle: "Global Impact at a Glance",
    verifiedData: "Verified data from JCI Impact Report 2025",
    globalMembers: "Global Members",
    activeWorldwide: "Active worldwide",
    projects: "Projects",
    period20242025: "Period: 2024-2025",
    volunteerHours: "Volunteer Hours",
    hoursContributed: "Hours contributed",
    countries: "Countries",
    worldwidePresence: "Worldwide presence",
    localOrganizations: "Local Organizations",
    activeCommunities: "Active communities",
    digitalReachTitle: "Digital Reach & Footprint",
    totalFollowers: "Total Followers",
    jciccUsers: "jci.cc Users",
    memberDemographicsTitle: "Member Demographics Snapshot",
    averageAge: "Average Age",
    yearsOld: "Years old",
    universityEducated: "University Educated",
    bachelorsOrHigher: "Bachelor's degree or higher",
    fullTime: "Full-time",
    entrepreneurs: "Entrepreneurs",
    employmentStatus: "Employment Status"
  },
  charts: {
    noDataAvailable: "No data available",
    dataLoadingSoon: "Data will be loaded soon",
    noFunnelData: "No funnel data available",
    configureProcessData: "Configure the process data",
    radarUnavailable: "Radar data unavailable",
    configurePerformanceIndicators: "Configure performance indicators",
    performanceUnavailable: "Performance data unavailable",
    configureTrendData: "Configure trend data",
    currentProgress: "Current Progress",
    goal2040: "Goal 2040",
    mainValue: "Main Value",
    trend: "Trend",
    goal: "Goal"
  },
  header: {
    title: "JCI Development Councils Youth Futures Observatory",
    subtitle: "Development Councils Global Observatory",
    tagline: "Developing Leaders for a Changing World"
  },
  dashboard: {
    heroTitle: "JCI Development Councils Youth Futures Observatory",
    heroSubtitle: "Measuring Youth Leadership, Mental Health & Economic Opportunity",
    heroDescription: "Understanding global youth opportunity through the Youth Futures Index (YFI)",
    learnAboutFII: "Learn About YFI",
    globalReach: "Global Reach",
    activeMembersWorldwide: "Active Members Worldwide",
    acrossCountries: "Across 114 Countries",
    localOrganizations: "Local Organizations",
    impactDelivered: "Impact Delivered",
    beneficiariesReached: "Beneficiaries Reached (2024-2025)",
    projectsImplemented: "Projects Implemented",
    volunteerHours: "Volunteer Hours Contributed",
    digitalPresence: "Digital Presence",
    socialFollowers: "Social Media Followers",
    monthlyImpressions: "Monthly Impressions",
    magazineReaders: "Magazine Readers (70+ countries)",
    websiteEngagement: "Website Engagement",
    activeUsers: "Active Users on jci.cc",
    pageViews: "Page Views",
    avgSessionDuration: "Avg Session Duration",
    whatIsFII: "What is the Youth Futures Index (YFI)?",
    fiiDescription: "The YFI measures youth opportunity across 114 countries using three weighted dimensions",
    mentalHealth: "Mental Health",
    mentalHealthDesc: "Mental health prevalence, suicide rates, access to services",
    workforcePreparation: "Workforce Preparation",
    workforceDesc: "Youth unemployment, skills development, educational attainment",
    entrepreneurship: "Entrepreneurship",
    entrepreneurshipDesc: "Business environment, entrepreneurship support, innovation",
    exploreFullIndex: "Explore the Full Index",
    memberExperience: "Member Experience Snapshot",
    basedOnSurvey: "Based on Member Survey (2025)",
    keyDemographics: "Key Demographics",
    averageAge: "Average Age",
    years: "years",
    genderDistribution: "Gender Distribution",
    male: "Male",
    female: "Female",
    other: "Other",
    employmentStatus: "Employment Status",
    fullTimeEmployed: "Full-time Employed",
    entrepreneurSelfEmployed: "Entrepreneur/Self-employed",
    student: "Student",
    education: "Education",
    universityDegrees: "University Degrees",
    regionalAgeRange: "Regional Age Range",
    youngest: "Youngest",
    oldest: "Oldest",
    membershipTenure: "Membership Tenure",
    newMembersJoining: "New members joining",
    buildingFoundation: "Building foundation",
    growingLeadership: "Growing leadership",
    experiencedMembers: "Experienced members",
    sustainedCommitment: "Sustained commitment",
    projectsAcrossRegions: "projects across 4 regions",
    demographicsMotivations: "Demographics & motivations",
    mentalHealthIndex: "Mental Health Index",
    fiiWeight: "of YFI • Global crisis data",
    fiiRankingsAnalysis: "YFI index & analysis",
    digitalReach: "Digital trends & innovation",
    howFIICalculated: "How YFI is calculated",
    conferencesPrograms: "JCI conferences & programs",
    strategicGrowthTargets: "Strategic growth targets",
    regionalCouncils: "AMDEC, ADC, APDC, EDC",
    howToContribute: "How to contribute",
    frequentlyAskedQuestions: "Frequently asked questions"
  },
  metrics: {
    membersActive: "Active Members",
    countriesPresent: "Countries Present",
    startupsSupported: "Startups Supported",
    sustainableProjects: "Sustainable Projects",
    completed: "completed",
    countries: "Countries",
    members: "Members",
    projects: "Projects",
    projectsPerYear: "Projects/year",
    memberDensity: "Member Density",
    country: "Country",
    regionalDistribution: "Regional Distribution",
    globalPresence: "Global JCI presence in 2025",
    activeMembers: "Active Members",
    globally: "globally",
    worldwidePresence: "worldwide presence",
    communities: "Communities",
    activeCommunities: "active communities",
    yearsOfHistory: "Years of History",
    since1915: "since 1915",
    vs2024: "vs 2024",
    annualProjects: "Annual Projects",
    in2025: "in 2025"
  },
  dashboardTenure: {
    lessThan1Year: "<1 year",
    years1to3: "1-3 years",
    years4to6: "4-6 years",
    years7to9: "7-9 years",
    moreThan10Years: ">10 years",
    respondents: "respondents"
  },
  homeGlobalPresence: {
    title: "JCI Global Presence",
    subtitle: "Active in 114 Countries across 4 Regions",
    regions: "Regions",
    countries: "Countries",
    members: "Members",
    localOrgs: "Local Orgs"
  },
  globalPresenceMap: {
    title: "JCI Global Presence",
    subtitle: "Active in 114 Countries across 4 Regions",
    countriesLabel: "countries",
    regionsLabel: "regions",
    countriesCount: "Countries",
    membersCount: "Members",
    localOrgs: "Local Organizations",
    regions: {
      asiaPacific: "Asia and the Pacific",
      europe: "Europe",
      africaMe: "Africa and the Middle East",
      americas: "America"
    }
  },
  observatoryHeader: {
    title: "JCI Futures Observatory",
    subtitle: "Development Councils Global Observatory",
    tagline: "Developing Leaders for a Changing World",
    members: "Members",
    countries: "Countries",
    beneficiaries: "Beneficiaries",
    projects: "Projects"
  },
  homeGlobalMap: {
    title: "JCI Global Presence",
    subtitle: "Young leaders active in 114 countries",
    members: "Members",
    countries: "Countries",
    localOrgs: "Local Orgs",
    click: "Click for details"
  },
  jciGlobalPresence: {
    title: "JCI Global Presence",
    subtitle: "Active in 114 Countries across 4 Regions",
    countries: "countries",
    orgs: "orgs",
    regions: "regions",
    africaMiddleEast: "Africa and the Middle East",
    americas: "America",
    asiaPacific: "Asia and the Pacific",
    europe: "Europe"
  },
  chartsAdvanced: {
    loading: "Loading chart...",
    noData: "No data available",
    error: "Failed to load chart",
    xAxis: "X Axis",
    yAxis: "Y Axis",
    legend: "Legend",
    tooltip: "Tooltip",
    zoom: "Zoom",
    pan: "Pan",
    reset: "Reset",
    download: "Download"
  },
  digitalEcosystem: {
    title: "JCI Digital Ecosystem",
    subtitle: "Digital platforms powering member engagement and outreach",
    website: "Website",
    socialMedia: "Social Media",
    magazine: "Magazine",
    activeUsers: "Active Users",
    followers: "Followers",
    readers: "Readers",
    engagement: "Engagement",
    reach: "Reach",
    impressions: "Impressions"
  },
  observatoryMastery: {
    title: "Observatory Mastery",
    modulesExplored: "Modules Explored",
    outOf: "of",
    observer: "Observer",
    analyst: "Analyst",
    visionary: "Visionary",
    keepExploring: "Keep exploring to unlock insights!",
    almostThere: "Almost there! You're becoming an expert.",
    masterAchieved: "You've mastered the Observatory!",
    unlockReward: "Visit all modules to unlock the Executive Summary",
    loginToTrack: "Log in to track your exploration progress across all 14 modules",
    signInToStart: "Sign in to start"
  },
  apiAnalytics: {
    title: "API Analytics",
    description: "Monitor API usage, performance, and trends",
    last24Hours: "Last 24 Hours",
    last7Days: "Last 7 Days",
    last30Days: "Last 30 Days",
    totalRequests: "Total Requests",
    successful: "successful",
    avgResponseTime: "Avg Response Time",
    acrossAllEndpoints: "Across all endpoints",
    errorRate: "Error Rate",
    errors: "errors",
    activeApiKeys: "Active API Keys",
    totalKeys: "total keys",
    requestsOverTime: "Requests Over Time",
    requestsByDay: "API requests by day",
    requestsByEndpoint: "Requests by Endpoint",
    distributionAcrossEndpoints: "Distribution across API endpoints",
    responseStatusCodes: "Response Status Codes",
    distributionOfStatusCodes: "Distribution of HTTP status codes",
    topApiKeys: "Top API Keys",
    keysWithHighestUsage: "Keys with highest usage",
    active: "Active",
    inactive: "Inactive",
    noApiKeysFound: "No API keys found"
  }
} as const;

